import type { ReactNode } from 'react';

type Trend = 'up' | 'down' | 'flat';

/**
 * Small headline metric block. Used in the BrandStats widget grid.
 *
 * `delta` is the change vs the prior window of the same length; we show it
 * with a colored chip. `goodDirection` lets a caller flip the colour mapping
 * for metrics like position where "lower is better".
 */
export function KpiCard({
  label,
  value,
  delta,
  goodDirection = 'up',
  hint,
}: {
  label: string;
  value: ReactNode;
  delta?: number | null;
  goodDirection?: 'up' | 'down';
  hint?: string;
}) {
  const trend: Trend = delta == null || delta === 0 ? 'flat' : delta > 0 ? 'up' : 'down';
  const isGood = trend === 'flat' ? null : (trend === 'up') === (goodDirection === 'up');

  const chipColor =
    isGood == null
      ? 'text-muted bg-surface-hover'
      : isGood
        ? 'text-success bg-accent-light'
        : 'text-danger bg-accent-light';

  const sign = delta != null && delta > 0 ? '+' : '';

  return (
    <div className="bg-surface-hover border border-border rounded-card px-4 py-3">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] uppercase tracking-wider text-muted font-medium">{label}</span>
        {delta != null && (
          <span className={`text-[11px] font-medium px-1.5 py-0.5 rounded ${chipColor}`}>
            {trend === 'flat' ? '—' : `${sign}${formatDelta(delta)}`}
          </span>
        )}
      </div>
      <div className="mt-1.5 text-2xl font-semibold text-primary tabular-nums leading-none">{value}</div>
      {hint && <div className="mt-1 text-[11px] text-muted">{hint}</div>}
    </div>
  );
}

function formatDelta(delta: number): string {
  const abs = Math.abs(delta);
  if (abs >= 100) return delta.toFixed(0);
  if (abs >= 10) return delta.toFixed(1);
  return delta.toFixed(2);
}
